import React, { useState, useEffect } from "react";
import { useTheme } from "@/context/ThemeContext";
import {
  Phone,
  ArrowRight,
  BarChart3,
  TrendingUp,
  Target,
  Zap,
  Users,
  CheckCircle2,
  ChevronDown,
  Star,
  Quote,
  DollarSign,
  Sparkles,
  LineChart,
  PhoneIncoming,
  MapPin,
  Printer,
  Palette,
  Database,
  UserCheck,
  Lightbulb,
  ShieldCheck,
} from "lucide-react";

const LeadGeneration = () => {
  const [customerCount, setCustomerCount] = useState(1000);
  const [avgInvoice, setAvgInvoice] = useState(500); // Average invoice amount
  const [responseRate, setResponseRate] = useState(1); // Response rate percentage (0.5-5%)
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [animatedReturn, setAnimatedReturn] = useState(0);
  const { theme } = useTheme();
  const isDark = theme === "dark";

  // CertainPath brand colors - burgundy/maroon gradient theme
  const colors = {
    primary: isDark ? "#CC4059" : "rgb(109, 21, 51)",
    primaryDark: isDark ? "#B21E34" : "rgb(78, 15, 36)",
    primaryLight: isDark ? "rgba(217, 76, 101, 0.1)" : "rgb(109, 21, 51, 0.1)",
    primaryLightSolid: isDark ? "rgba(217, 76, 101, 0.15)" : "#F9E8ED",
    gradient:
      "bg-gradient-to-br from-gray-900 via-[rgb(109,21,51)] to-[rgb(78,15,36)]",
  };

  const testimonials = [
    {
      quote:
        "We've tried everything - Facebook ads, Google PPC, shared mailers. Nothing has given us the consistent ROI that HUB+ Direct provides. It's now our #1 lead source.",
      name: "Mike Thompson",
      company: "Apex Plumbing",
      location: "Denver, CO",
      revenue: "$124k",
      roi: "12:1",
      image: "/images/testimonial-mike.png",
    },
    {
      quote:
        "The ability to target only the homeowners we want is a game changer. We're not wasting money on renters or people who can't afford our services.",
      name: "Sarah Lewis",
      company: "Comfort Air HVAC",
      location: "Phoenix, AZ",
      revenue: "$89k",
      roi: "15:1",
      image: "/images/testimonial-sarah.png",
    },
    {
      quote:
        "I was skeptical at first, but the match-back reports proved it works. Seeing exactly which customers called from the postcards gave me total confidence.",
      name: "David Rodriguez",
      company: "Rooter Pro",
      location: "Chicago, IL",
      revenue: "$210k",
      roi: "18:1",
      image: "/images/testimonial-david.png",
    },
  ];

  const faqs = [
    {
      question: "How quickly can we get started?",
      answer:
        "Most contractors are up and running within 2 weeks. We handle all the targeting, data selection, design, and printing for you.",
    },
    {
      question: "How do you find the right prospects?",
      answer:
        "We use key demographic data points including home value, age, income, and more to identify homeowners most likely to need your services. No more spray-and-pray marketing.",
    },
    {
      question: "How do you track which new customers came from the mailings?",
      answer:
        "We use match-back reporting to connect new customer revenue directly to mail recipients. You'll see exactly which prospects became customers and how much they spent.",
    },
    {
      question: "What response rate can I expect?",
      answer:
        "Prospect mailings typically see 0.5-2% response rates, depending on your offer, service type, and market. We continuously optimize targeting to improve your results over time.",
    },
    {
      question: "Are there any long-term contracts?",
      answer:
        "No contracts required. We're confident in our results, so you can cancel anytime if you're not satisfied.",
    },
  ];

  // Cost per mailer
  const costPerMailer = 0.85;
  const estimatedCost = Math.round(customerCount * costPerMailer);

  // ROI Calculator - based on user-adjustable response rate and avg invoice
  const responseRateDecimal = responseRate / 100;
  const expectedResponses = Math.round(customerCount * responseRateDecimal);
  const expectedRevenue = expectedResponses * avgInvoice;
  const roiMultiplier =
    estimatedCost > 0 ? (expectedRevenue / estimatedCost).toFixed(1) : 0;

  // Get response rate tier label
  const getResponseTier = (rate: number) => {
    if (rate <= 1.5) return { label: "Conservative", color: "#6b7280" };
    if (rate <= 2.5) return { label: "Typical", color: "#059669" };
    if (rate <= 3.5) return { label: "Strong", color: "#0891b2" };
    return { label: "Aggressive", color: "#7c3aed" };
  };

  // Animate return number when it changes
  useEffect(() => {
    const targetReturn = expectedRevenue;
    const duration = 800;
    const steps = 30;
    const stepTime = duration / steps;

    let currentStep = 0;
    const startReturn = animatedReturn;

    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      const easeOut = 1 - Math.pow(1 - progress, 3);

      setAnimatedReturn(
        Math.round(startReturn + (targetReturn - startReturn) * easeOut),
      );

      if (currentStep >= steps) {
        clearInterval(timer);
        setAnimatedReturn(targetReturn);
      }
    }, stepTime);

    return () => clearInterval(timer);
  }, [customerCount, avgInvoice, responseRate]);

  return (
    <div className="min-h-screen relative overflow-hidden bg-gray-50 dark:bg-slate-950">
      {/* Subtle Animated Background - Floating Blobs */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        {/* Blob 1 - Burgundy */}
        <div
          className="absolute w-96 h-96 rounded-full opacity-[0.03] blur-3xl"
          style={{
            background:
              "radial-gradient(circle, rgb(109, 21, 51) 0%, transparent 70%)",
            animation: "float1 25s ease-in-out infinite",
            top: "10%",
            left: "5%",
          }}
        />
        {/* Blob 2 - Dark Maroon */}
        <div
          className="absolute w-80 h-80 rounded-full opacity-[0.03] blur-3xl"
          style={{
            background:
              "radial-gradient(circle, rgb(78, 15, 36) 0%, transparent 70%)",
            animation: "float2 30s ease-in-out infinite",
            top: "60%",
            right: "10%",
          }}
        />
        {/* Blob 3 - Gray */}
        <div
          className="absolute w-72 h-72 rounded-full opacity-[0.05] blur-3xl"
          style={{
            background: "radial-gradient(circle, #374151 0%, transparent 70%)",
            animation: "float3 28s ease-in-out infinite",
            bottom: "20%",
            left: "15%",
          }}
        />
      </div>
      <style>{`
        @keyframes float1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 30px) scale(0.9); }
        }
        @keyframes float2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(-40px, 40px) scale(1.15); }
          66% { transform: translate(30px, -30px) scale(0.85); }
        }
        @keyframes float3 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(50px, 30px) scale(0.9); }
          66% { transform: translate(-30px, -40px) scale(1.1); }
        }
        @keyframes heroGradient {
          0%, 100% {
            background: radial-gradient(ellipse at 30% 20%, rgba(109, 21, 51, 0.6) 0%, transparent 50%), radial-gradient(ellipse at 70% 80%, rgba(78, 15, 36, 0.6) 0%, transparent 50%);
          }
          50% {
            background: radial-gradient(ellipse at 70% 30%, rgba(109, 21, 51, 0.6) 0%, transparent 50%), radial-gradient(ellipse at 30% 70%, rgba(78, 15, 36, 0.6) 0%, transparent 50%);
          }
        }
        @keyframes shimmer {
          0% { background-position: -200% -200%; }
          100% { background-position: 200% 200%; }
        }
      `}</style>

      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-[rgb(109,21,51)] to-[rgb(78,15,36)]">
        {/* Animated gradient overlay */}
        <div
          className="absolute inset-0 opacity-40"
          style={{
            background:
              "radial-gradient(ellipse at 30% 20%, rgba(109, 21, 51, 0.6) 0%, transparent 50%), radial-gradient(ellipse at 70% 80%, rgba(78, 15, 36, 0.6) 0%, transparent 50%)",
            animation: "heroGradient 15s ease-in-out infinite",
          }}
        />
        {/* Subtle shimmer effect */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            background:
              "linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%)",
            backgroundSize: "200% 200%",
            animation: "shimmer 8s ease-in-out infinite",
          }}
        />
        {/* Top border line */}
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
        {/* Bottom border line */}
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

        <div className="max-w-6xl mx-auto px-6 py-12 md:py-16 relative z-10">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-light text-white mb-6 leading-tight tracking-wide">
              Fill Your Schedule with
              <br />
              <span className="font-normal">New Customers</span>
            </h1>

            <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto mb-8 leading-relaxed font-light">
              Data-powered postcards that put your business in front of{" "}
              <span className="font-medium text-white">
                high-value homeowners
              </span>{" "}
              ready to buy
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
              <a
                className="group px-6 py-3 rounded-md font-semibold text-sm transition-all duration-200 inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-100"
                href="https://mycertainpath.com/book-a-call/"
                rel="noopener noreferrer"
                style={{ color: "rgb(109, 21, 51)" }}
                target="_blank"
              >
                <Phone className="w-4 h-4" />
                Schedule Growth Call
              </a>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-300 font-light">
              <span className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-white/70" />
                No Setup Fees
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-white/70" />
                No Contracts
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-white/70" />
                Performance Based
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Why Use Our System */}
      <div className="max-w-4xl mx-auto px-6 pt-20 mb-32 text-center">
        <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-8 tracking-tight">
          Why use our prospect marketing system?
        </h2>
        <p className="text-xl text-gray-600 dark:text-gray-400 leading-relaxed font-light">
          For over 15 years, we have helped contractors nationwide acquire new
          customers with precision-targeted direct mail. We combine{" "}
          <span className="font-semibold text-gray-900 dark:text-white">
            key demographic data points
          </span>{" "}
          to identify homeowners most likely to need your services—and put your
          offer directly in their hands.
        </p>
      </div>

      <div className="max-w-6xl mx-auto px-6">
        {/* ROI Estimator - Redesigned */}
        <div className="mb-32">
          <div className="bg-gradient-to-br from-gray-900 via-[rgb(109,21,51)] to-[rgb(78,15,36)] rounded-3xl p-8 md:p-12 relative overflow-hidden">
            {/* Top border line */}
            <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
            {/* Bottom border line */}
            <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

            <div className="relative z-10">
              {/* Header */}
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full text-white/80 text-sm font-medium mb-6 backdrop-blur-sm">
                  <Sparkles className="w-4 h-4" />
                  Interactive Calculator
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight">
                  Calculate Your New Customer Revenue
                </h2>
                <p className="text-lg text-white/70 max-w-2xl mx-auto">
                  See how many new customers you could acquire with targeted
                  direct mail
                </p>
              </div>

              {/* Main Content Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Left Side - Controls */}
                <div className="lg:col-span-5 space-y-6">
                  {/* Households to Mail Slider */}
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                    <div className="flex items-center justify-between mb-4">
                      <label className="text-white font-semibold flex items-center gap-2">
                        <Users className="w-5 h-5 text-pink-300" />
                        Households to Mail
                      </label>
                      <span className="text-3xl font-bold text-white">
                        {customerCount.toLocaleString()}
                      </span>
                    </div>
                    <input
                      className="w-full h-3 rounded-lg appearance-none cursor-pointer"
                      max="10000"
                      min="100"
                      onChange={(e) => setCustomerCount(Number(e.target.value))}
                      step="100"
                      style={{
                        background: `linear-gradient(to right, rgb(109, 21, 51) 0%, rgb(109, 21, 51) ${((customerCount - 100) / 9900) * 100}%, rgba(255,255,255,0.2) ${((customerCount - 100) / 9900) * 100}%, rgba(255,255,255,0.2) 100%)`,
                      }}
                      type="range"
                      value={customerCount}
                    />
                    <div className="flex justify-between text-xs text-white/50 mt-2">
                      <span>100</span>
                      <span>10,000</span>
                    </div>
                  </div>

                  {/* Average Invoice Amount */}
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                    <div className="flex items-center justify-between mb-4">
                      <label className="text-white font-semibold flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-green-400" />
                        Average Invoice
                      </label>
                      <span className="text-3xl font-bold text-white">
                        ${avgInvoice.toLocaleString()}
                      </span>
                    </div>
                    <input
                      className="w-full h-3 rounded-lg appearance-none cursor-pointer"
                      max="5000"
                      min="100"
                      onChange={(e) => setAvgInvoice(Number(e.target.value))}
                      step="50"
                      style={{
                        background: `linear-gradient(to right, #22c55e 0%, #22c55e ${((avgInvoice - 100) / 4900) * 100}%, rgba(255,255,255,0.2) ${((avgInvoice - 100) / 4900) * 100}%, rgba(255,255,255,0.2) 100%)`,
                      }}
                      type="range"
                      value={avgInvoice}
                    />
                    <div className="flex justify-between text-xs text-white/50 mt-2">
                      <span>$100</span>
                      <span>$5,000</span>
                    </div>
                    <p className="text-xs text-white/40 mt-3 text-center">
                      What&apos;s your typical service call worth?
                    </p>
                  </div>

                  {/* Expected Response Rate (ROI Dial) */}
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-white font-semibold flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-cyan-400" />
                        Expected Response
                      </label>
                      <div className="flex items-center gap-2">
                        <span className="text-3xl font-bold text-white">
                          {responseRate}%
                        </span>
                        <span
                          className="text-xs px-2 py-1 rounded-full font-medium"
                          style={{
                            backgroundColor: `${getResponseTier(responseRate).color}20`,
                            color: getResponseTier(responseRate).color,
                          }}
                        >
                          {getResponseTier(responseRate).label}
                        </span>
                      </div>
                    </div>
                    <input
                      className="w-full h-3 rounded-lg appearance-none cursor-pointer mt-4"
                      max="5"
                      min="0.5"
                      onChange={(e) => setResponseRate(Number(e.target.value))}
                      step="0.5"
                      style={{
                        background: `linear-gradient(to right,
                          #6b7280 0%,
                          #059669 ${((1.5 - 0.5) / 4.5) * 100}%,
                          #0891b2 ${((3 - 0.5) / 4.5) * 100}%,
                          #7c3aed 100%)`,
                      }}
                      type="range"
                      value={responseRate}
                    />
                    <div className="flex justify-between text-xs text-white/50 mt-2">
                      <span>0.5%</span>
                      <span>2.5%</span>
                      <span>5%</span>
                    </div>
                    <p className="text-xs text-white/40 mt-3 text-center">
                      Industry avg is 0.5-2% for prospect mailings
                    </p>
                  </div>

                  {/* Investment Summary */}
                  <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-green-400" />
                      </div>
                      <div>
                        <div className="text-white/60 text-sm">
                          Your Investment
                        </div>
                        <div className="text-2xl font-bold text-white">
                          ${estimatedCost.toLocaleString()}
                          <span className="text-base font-normal text-white/50">
                            /campaign
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="text-sm text-white/50">
                      ~${costPerMailer.toFixed(2)} per mailer ×{" "}
                      {customerCount.toLocaleString()} households
                    </div>
                  </div>
                </div>

                {/* Right Side - Simple ROI Message */}
                <div className="lg:col-span-7">
                  <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 h-full flex flex-col justify-center">
                    {/* The Big Message */}
                    <div className="text-center mb-10">
                      <div className="text-gray-500 dark:text-gray-400 text-lg font-medium mb-4">
                        The Simple Math
                      </div>
                      <div
                        className="text-6xl md:text-7xl font-bold mb-6"
                        style={{ color: colors.primary }}
                      >
                        {roiMultiplier}x
                      </div>
                      <div className="text-2xl text-gray-700 dark:text-gray-300 font-medium">
                        For every{" "}
                        <span className="font-bold text-gray-900 dark:text-white">
                          $1
                        </span>{" "}
                        you spend
                      </div>
                      <div className="text-2xl text-gray-700 dark:text-gray-300 font-medium">
                        you get{" "}
                        <span
                          className="font-bold"
                          style={{ color: colors.primary }}
                        >
                          ${roiMultiplier}
                        </span>{" "}
                        back in sales
                      </div>
                    </div>

                    {/* Visual Breakdown */}
                    <div className="bg-gray-50 dark:bg-slate-700 rounded-2xl p-6 mb-8">
                      <div className="flex items-center justify-center gap-4 text-center">
                        {/* You Spend */}
                        <div className="flex-1">
                          <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                            You Spend
                          </div>
                          <div className="text-3xl font-bold text-gray-900 dark:text-white">
                            ${estimatedCost.toLocaleString()}
                          </div>
                        </div>

                        {/* Arrow */}
                        <div className="flex items-center justify-center">
                          <ArrowRight className="w-8 h-8 text-gray-300 dark:text-gray-500" />
                        </div>

                        {/* You Get */}
                        <div className="flex-1">
                          <div
                            className="text-sm mb-2"
                            style={{ color: colors.primary }}
                          >
                            You Get Back
                          </div>
                          <div
                            className="text-3xl font-bold"
                            style={{ color: colors.primary }}
                          >
                            ${animatedReturn.toLocaleString()}
                          </div>
                        </div>
                      </div>

                      {/* How it works */}
                      <div className="mt-6 pt-6 border-t border-gray-200 dark:border-slate-600 text-center">
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          Based on a{" "}
                          <span className="font-semibold">
                            {responseRate}% response rate
                          </span>{" "}
                          from {customerCount.toLocaleString()} targeted
                          households
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          ={" "}
                          <span className="font-semibold">
                            {expectedResponses} new customers
                          </span>{" "}
                          ×{" "}
                          <span className="font-semibold">
                            ${avgInvoice.toLocaleString()}
                          </span>{" "}
                          avg. invoice
                        </div>
                      </div>
                    </div>

                    {/* Net Profit */}
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-5 mb-8">
                      <div className="flex items-center justify-center gap-6">
                        <div className="text-center">
                          <div className="text-sm text-green-700 dark:text-green-400 font-medium mb-1">
                            Your Profit
                          </div>
                          <div className="text-3xl font-bold text-green-700 dark:text-green-400">
                            $
                            {(expectedRevenue - estimatedCost).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* CTA */}
                    <button
                      className="w-full py-4 rounded-xl font-semibold text-white flex items-center justify-center gap-2 transition-all duration-200 hover:opacity-90 hover:scale-[1.02]"
                      style={{ backgroundColor: "rgb(109, 21, 51)" }}
                      type="button"
                    >
                      <Phone className="w-5 h-5" />
                      Get Your Personalized Analysis
                      <ArrowRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Why Contractors Choose Us */}
        <div className="mb-32">
          <div className="text-center mb-16">
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6"
              style={{
                backgroundColor: colors.primaryLightSolid,
                color: colors.primary,
              }}
            >
              <ShieldCheck className="w-4 h-4" />
              Trusted by 500+ Contractors
            </div>
            <h2 className="text-5xl font-bold text-gray-900 dark:text-white mb-6 tracking-tight">
              Why Contractors Choose Us
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Everything you need to acquire new customers and grow your
              business
            </p>
          </div>

          {/* Featured Benefits - Top Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            {[
              {
                icon: <LineChart className="w-7 h-7" />,
                title: "Market Benchmarking",
                description:
                  "See how you stack up against competitors with our exclusive MEER analytics dashboard.",
                stat: "Real-time",
                statLabel: "market data",
                color: "#7c3aed",
              },
              {
                icon: <TrendingUp className="w-7 h-7" />,
                title: "ROI Match-Back Reports",
                description:
                  "Know exactly which mailers drove revenue. Every dollar tracked, every customer matched.",
                stat: "100%",
                statLabel: "attribution",
                color: "#059669",
              },
              {
                icon: <PhoneIncoming className="w-7 h-7" />,
                title: "AI Call Intelligence",
                description:
                  "Our AI listens to every call, scoring quality and flagging missed booking opportunities.",
                stat: "AI-Powered",
                statLabel: "call scoring",
                color: "#0891b2",
              },
            ].map((benefit, index) => (
              <div
                className="group bg-white dark:bg-slate-800 rounded-2xl p-8 border border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600 hover:shadow-lg transition-all duration-300 relative overflow-hidden"
                key={index}
              >
                {/* Accent bar */}
                <div
                  className="absolute top-0 left-0 right-0 h-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{ backgroundColor: benefit.color }}
                />

                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 transition-transform duration-300 group-hover:scale-110"
                  style={{
                    backgroundColor: `${benefit.color}15`,
                    color: benefit.color,
                  }}
                >
                  {benefit.icon}
                </div>

                <h3 className="font-bold text-xl text-gray-900 dark:text-white mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-5">
                  {benefit.description}
                </p>

                <div className="pt-4 border-t border-gray-100 dark:border-slate-700">
                  <span
                    className="text-2xl font-bold"
                    style={{ color: benefit.color }}
                  >
                    {benefit.stat}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">
                    {benefit.statLabel}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Secondary Benefits - Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                icon: <MapPin className="w-5 h-5" />,
                title: "Precision Targeting",
                description:
                  "Reach homeowners by income, home value, and age—not just zip codes.",
                highlight: "200+ data points",
              },
              {
                icon: <Printer className="w-5 h-5" />,
                title: "Premium Printing",
                description:
                  "Thick card stock with vibrant inkjet colors that stand out in the mailbox.",
                highlight: "Full color",
              },
              {
                icon: <Palette className="w-5 h-5" />,
                title: "Seasonal Creative",
                description:
                  "Fresh designs every campaign, tailored to seasons and service types.",
                highlight: "Auto-updated",
              },
              {
                icon: <Database className="w-5 h-5" />,
                title: "Smart Data Hygiene",
                description:
                  "We clean your list—no more wasting money on moved or deceased contacts.",
                highlight: "Save 15-20%",
              },
              {
                icon: <UserCheck className="w-5 h-5" />,
                title: "Dedicated Success Manager",
                description:
                  "One expert handles everything. No phone trees, no runaround.",
                highlight: "Direct line",
              },
              {
                icon: <Lightbulb className="w-5 h-5" />,
                title: "Strategy Guidance",
                description:
                  "Data-driven recommendations to optimize timing, offers, and targeting.",
                highlight: "15+ years exp.",
              },
              {
                icon: <Zap className="w-5 h-5" />,
                title: "Fast Turnaround",
                description:
                  "From approval to mailboxes in under 2 weeks. No delays, no excuses.",
                highlight: "< 14 days",
              },
              {
                icon: <ShieldCheck className="w-5 h-5" />,
                title: "No Contracts",
                description:
                  "Month-to-month. If we don't deliver results, you walk. Simple as that.",
                highlight: "Zero risk",
              },
            ].map((benefit, index) => (
              <div
                className="group bg-white dark:bg-slate-800 rounded-xl p-5 border border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600 hover:shadow-md transition-all duration-200"
                key={index}
              >
                <div className="flex items-start gap-4">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors duration-200"
                    style={{
                      backgroundColor: colors.primaryLightSolid,
                      color: colors.primary,
                    }}
                  >
                    {benefit.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                        {benefit.title}
                      </h3>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-2">
                      {benefit.description}
                    </p>
                    <span
                      className="inline-block text-xs font-semibold px-2 py-1 rounded-full"
                      style={{
                        backgroundColor: colors.primaryLightSolid,
                        color: colors.primary,
                      }}
                    >
                      {benefit.highlight}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* How It Works - Visual Journey */}
        <div className="mb-32">
          <h2 className="text-5xl font-bold text-gray-900 dark:text-white mb-6 text-center tracking-tight">
            How It Works
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 mb-20 text-center max-w-2xl mx-auto font-light">
            A proven 4-step process to acquire new customers and grow your
            business
          </p>

          <div className="relative">
            {/* Connecting Line */}
            <div
              className="hidden md:block absolute top-8 left-0 right-0 h-1 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-slate-700 dark:via-slate-600 dark:to-slate-700"
              style={{ top: "60px" }}
            />

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative">
              {[
                {
                  title: "We Build Your Audience",
                  description:
                    "Using 200+ data points, we identify homeowners most likely to need your services",
                  icon: <BarChart3 className="w-8 h-8" />,
                },
                {
                  title: "Targeted Mailings",
                  description:
                    "Premium postcards reach high-value prospects in your service area every 6 weeks",
                  icon: <Zap className="w-8 h-8" />,
                },
                {
                  title: "Track New Customers",
                  description:
                    "Match-back reporting shows exactly which new customers came from your mailings",
                  icon: <TrendingUp className="w-8 h-8" />,
                },
                {
                  title: "Continuous Optimization",
                  description:
                    "Your success manager refines targeting based on which demographics convert best",
                  icon: <Target className="w-8 h-8" />,
                },
              ].map((step, index) => (
                <div className="relative" key={index}>
                  <div className="bg-white dark:bg-slate-800 rounded-2xl p-10 border border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600 transition-all duration-200">
                    <div className="flex justify-center mb-6">
                      <div
                        className="w-14 h-14 rounded-full flex items-center justify-center font-bold text-white text-xl"
                        style={{ backgroundColor: colors.primary }}
                      >
                        {index + 1}
                      </div>
                    </div>
                    <div
                      className="flex justify-center mb-5"
                      style={{ color: colors.primary }}
                    >
                      {step.icon}
                    </div>
                    <h3 className="font-semibold text-lg text-gray-900 dark:text-white mb-3 text-center">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 text-center leading-relaxed text-sm">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Testimonials */}
        <div className="mb-32">
          <h2 className="text-5xl font-bold text-gray-900 dark:text-white mb-6 text-center tracking-tight">
            Success Stories
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 mb-20 text-center max-w-2xl mx-auto font-light">
            Real results from contractors just like you
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                className="bg-white dark:bg-slate-800 rounded-2xl p-10 border border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600 transition-all duration-200"
                key={index}
              >
                <Quote
                  className="w-10 h-10 mb-4 opacity-20"
                  style={{ color: colors.primary }}
                />

                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      className="w-5 h-5 fill-current"
                      key={i}
                      style={{ color: colors.primary }}
                    />
                  ))}
                </div>

                <p className="text-gray-700 dark:text-gray-300 mb-6 italic leading-relaxed">
                  &ldquo;{testimonial.quote}&rdquo;
                </p>

                <div className="border-t border-gray-200 dark:border-slate-700 pt-6">
                  <div className="flex items-center gap-4 mb-4">
                    <img
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover border border-gray-200 dark:border-slate-600"
                      src={testimonial.image}
                    />
                    <div>
                      <div className="font-bold text-gray-900 dark:text-white text-lg leading-tight">
                        {testimonial.name}
                      </div>
                      <div className="text-gray-600 dark:text-gray-400 text-sm">
                        {testimonial.company} • {testimonial.location}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-1 bg-gray-50 dark:bg-slate-700 rounded-lg p-3 text-center">
                      <div className="text-sm text-gray-500 dark:text-gray-400 font-semibold">
                        Revenue
                      </div>
                      <div
                        className="text-xl font-bold"
                        style={{ color: colors.primary }}
                      >
                        {testimonial.revenue}
                      </div>
                    </div>
                    <div className="flex-1 bg-gray-50 dark:bg-slate-700 rounded-lg p-3 text-center">
                      <div className="text-sm text-gray-500 dark:text-gray-400 font-semibold">
                        ROI
                      </div>
                      <div
                        className="text-xl font-bold"
                        style={{ color: colors.primary }}
                      >
                        {testimonial.roi}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mb-32">
          <h2 className="text-5xl font-bold text-gray-900 dark:text-white mb-6 text-center tracking-tight">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 mb-16 text-center max-w-2xl mx-auto font-light">
            Everything you need to know about HUB+ Direct
          </p>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div
                className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden"
                key={index}
              >
                <button
                  className="w-full px-8 py-6 text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  type="button"
                >
                  <span className="font-bold text-lg text-gray-900 dark:text-white pr-8">
                    {faq.question}
                  </span>
                  <ChevronDown
                    className={`w-6 h-6 flex-shrink-0 transition-transform duration-300 ${openFaq === index ? "rotate-180" : ""}`}
                    style={{ color: colors.primary }}
                  />
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openFaq === index ? "max-h-96" : "max-h-0"
                  }`}
                >
                  <div className="px-8 pb-6 text-gray-600 dark:text-gray-400 leading-relaxed">
                    {faq.answer}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Final CTA */}
        <div className="rounded-3xl p-16 text-center mb-12 relative overflow-hidden bg-gradient-to-br from-gray-900 via-[rgb(109,21,51)] to-[rgb(78,15,36)]">
          {/* Top border line */}
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
          {/* Bottom border line */}
          <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

          <div className="relative">
            <Users className="w-16 h-16 text-white mx-auto mb-6 opacity-70" />
            <h2 className="text-4xl md:text-5xl font-light text-white mb-6 tracking-wide">
              Ready to Grow Your Customer Base?
            </h2>
            <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto leading-relaxed font-light">
              Get a free market analysis showing how many new customers are
              waiting in your service area
            </p>

            <div className="flex flex-col sm:flex-row gap-5 justify-center">
              <a
                className="group bg-white px-10 py-5 rounded-md font-semibold text-lg transition-all duration-300 inline-flex items-center justify-center gap-3 hover:bg-gray-100"
                href="https://mycertainpath.com/book-a-call/"
                rel="noopener noreferrer"
                style={{ color: "rgb(109, 21, 51)" }}
                target="_blank"
              >
                <Phone className="w-6 h-6 group-hover:rotate-12 transition-transform" />
                Schedule Growth Call
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadGeneration;
